//TODO: delete this file after new pricing migration
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import { Disclosure, Transition } from '@headlessui/react';

import type {
    UserSubscriptionType,
    OptionsAvailableType
} from '@/utils/hooks/swr/useBillingSubscription';

import useBillingSubscription from '@/utils/hooks/swr/useBillingSubscription';
import useFormatNumber from '@/hooks/format/useFormatNumber';
import useUser from '@/utils/hooks/swr/useUser';

//components
import H2 from '@/components/base/Headings/H2';
import Icon from '@/components/base/Icon';
import { OldPlanOptions } from '@/components/pages/settings/subscription-invoices/CurrentUserPlan/OldPricing/OldPlanOptions';
// import { MigrationOldPlanBanner } from '@/components/pages/settings/subscription-invoices/CurrentUserPlan/OldPricing/MigrationBanner';

//libs
import { TRIAL } from '@/lib/plans';

const wrapperClasses =
    'bg-icoGray-100 dark:bg-icoDarkMode-deepBlack rounded-md min-h-[11.25rem] p-8';

export function OldCurrentPlan() {
    const { t } = useTranslation('settings');
    const {
        isLoading,
        billingSubscriptionError,
        mutateBillingSubscription,
        plan: userPlan
    } = useBillingSubscription({
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { user } = useUser();
    const { formatPriceCurrencySymbol } = useFormatNumber(user?.settings?.numbers_format);

    if (isLoading)
        return (
            <div className={clsx('animate-pulse', wrapperClasses)} data-testid="loading-plan"></div>
        );

    if (billingSubscriptionError) {
        return (
            <div
                className={clsx(
                    'flex flex-col items-center justify-center gap-1 text-sm',
                    wrapperClasses
                )}
                data-testid="error-plan">
                <p className="text-center">{t('errors:error_occured_fetch_data')} </p>
                <button
                    className="text-icoBlue underline"
                    onClick={() => mutateBillingSubscription()}
                    data-testid="relaunch-fetch">
                    {t('commons:try_again')}
                </button>
            </div>
        );
    }

    const plan = userPlan as UserSubscriptionType;

    return (
        <div
            className={clsx('flex flex-col space-y-2 @container', wrapperClasses)}
            data-testid="current-plan-wrapper">
            <div className="flex flex-col-reverse gap-2 @sm:flex-row @sm:justify-between">
                <div className="flex flex-col space-y-2">
                    <H2>
                        {t(`subscription_invoices_page.${plan?.product_key ?? 'no_active_plan'}`)}
                    </H2>
                    {plan?.is_apple_subscription ? (
                        <span className="mt-1" data-testid="apple-subscription">
                            {t('subscription_invoices_page.apple_plan')}
                        </span>
                    ) : null}
                    {plan?.product_key && plan?.product_key !== TRIAL ? (
                        <div
                            className="flex flex-col gap-1 text-lg leading-none text-icoGray-500 dark:text-icoDarkMode-wolf"
                            data-testid="plan-price">
                            <span>
                                {t(
                                    `subscription_invoices_page.price_per_${
                                        plan?.frequency === 'yearly' ? 'year' : 'month'
                                    }`,
                                    {
                                        price: formatPriceCurrencySymbol(
                                            plan?.total,
                                            plan?.currency
                                        )
                                    }
                                )}
                            </span>
                            <span>{t('subscription_invoices_page.tax_included')}</span>
                        </div>
                    ) : null}
                </div>
                {/* Keep this since we'll need to display it at some point */}
                {/* {userPlan?.product_key ? (
                    <div className="@sm:w-72">
                        <MigrationOldPlanBanner />
                    </div>
                ) : null} */}
            </div>
            {plan?.product_key ? (
                <Disclosure>
                    {({ open }) => (
                        <>
                            <Disclosure.Button
                                className="flex items-center space-x-2.5 pb-0.5 text-left text-icoBlue focus:ring-0 focus:ring-offset-0"
                                data-testid="plan-details-toggle">
                                <span className="text-sm underline">
                                    {t('settings:subscription_invoices_page.show_plan_details')}
                                </span>
                                <Icon
                                    className="text-xs"
                                    icon={clsx({ 'chevron-bottom': open, 'chevron-top': !open })}
                                />
                            </Disclosure.Button>
                            <Transition
                                enter="transition duration-100 ease-out"
                                enterFrom="transform scale-95 opacity-0"
                                enterTo="transform scale-100 opacity-100"
                                leave="transition duration-75 ease-out"
                                leaveFrom="transform scale-100 opacity-100"
                                leaveTo="transform scale-95 opacity-0">
                                <Disclosure.Panel>
                                    <div
                                        className="mb-8 mt-6 space-y-4 text-sm"
                                        data-testid="plan-details">
                                        {Object.entries(plan?.options)?.map(
                                            ([optionKey, optionValues]) => {
                                                return (
                                                    <OldPlanOptions
                                                        key={optionKey}
                                                        optionKey={
                                                            optionKey as OptionsAvailableType
                                                        }
                                                        optionValues={optionValues}
                                                        currency={plan?.currency}
                                                        isManualPlan={plan?.is_manual}
                                                    />
                                                );
                                            }
                                        )}
                                    </div>
                                </Disclosure.Panel>
                            </Transition>
                        </>
                    )}
                </Disclosure>
            ) : null}
        </div>
    );
}
